// Shell-wide UI state: presenting mode, the open palette and switcher, and the side panel.
// Presenting mutes the Inbox badge and the `g` chords on a projector (note §6). Sidebar width,
// collapse and the nav drawer live in `@companyos/ui`'s AppShell (`useShellLayout`).
import { createContext, type ReactNode, useContext, useMemo, useState } from "react";

export type ShellState = {
  presenting: boolean;
  setPresenting: (value: boolean) => void;
  commandOpen: boolean;
  setCommandOpen: (value: boolean) => void;
  switcherOpen: boolean;
  setSwitcherOpen: (value: boolean) => void;
  panelOpen: boolean;
  setPanelOpen: (value: boolean) => void;
  panelTab: string | null;
  setPanelTab: (value: string | null) => void;
};

const ShellStateContext = createContext<ShellState | null>(null);

export function ShellStateProvider({ children }: { children: ReactNode }) {
  const [presenting, setPresenting] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelTab, setPanelTab] = useState<string | null>(null);

  const value = useMemo<ShellState>(
    () => ({
      presenting,
      setPresenting,
      commandOpen,
      setCommandOpen,
      switcherOpen,
      setSwitcherOpen,
      panelOpen,
      setPanelOpen,
      panelTab,
      setPanelTab,
    }),
    [presenting, commandOpen, switcherOpen, panelOpen, panelTab],
  );
  return <ShellStateContext.Provider value={value}>{children}</ShellStateContext.Provider>;
}

export function useShellState(): ShellState {
  const value = useContext(ShellStateContext);
  if (!value) throw new Error("useShellState must be used inside ShellStateProvider");
  return value;
}
